import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/use-async';
import { AdapterInfo, ApiModule, Credential, IdentityRow, SpecDiff, SpecVersion } from '../lib/types';
import { formatDateTime, relativeTime } from '../lib/format';
import { Badge, Card, ConfirmDelete, Empty, Field, Modal, Notice, PageHead, Tabs } from '../components/ui';

type Tab = 'specifications' | 'credentials' | 'operations' | 'identities';

interface Operation {
  id: string;
  operation_key: string;
  resource: string;
  action: string;
  http_method: string;
  path_template: string;
  is_write: boolean;
  supports_impersonation: boolean;
  summary: string | null;
  deprecated: boolean;
}

export function ModuleDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('specifications');
  const [deleting, setDeleting] = useState(false);
  const module = useAsync(() => api.get<ApiModule>(`/api/admin/modules/${id}`), [id]);
  const adapters = useAsync(() => api.get<AdapterInfo[]>('/api/admin/modules/adapters'));

  // Only where the target API can attribute a write does a mapping change
  // anything - elsewhere the tab would promise a decision it never makes.
  const canImpersonate = Boolean(
    adapters.data?.find((entry) => entry.key === module.data?.adapter)?.supportsImpersonation,
  );

  if (module.error) return <Notice tone="danger">{module.error}</Notice>;
  if (!module.data) return <p className="muted">Loading...</p>;

  return (
    <>
      <PageHead
        title={module.data.name}
        description={`Path /gw/${module.data.key} - adapter ${module.data.adapter}`}
        actions={
          <>
            <a className="button" href={`/docs/${module.data.key}`} target="_blank" rel="noreferrer">
              Open documentation
            </a>
            <button className="danger" onClick={() => setDeleting(true)}>
              Delete module
            </button>
          </>
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'specifications', label: 'Specifications' },
          { id: 'credentials', label: 'Credentials' },
          { id: 'operations', label: 'Operations' },
          ...(canImpersonate ? [{ id: 'identities' as const, label: 'Identities' }] : []),
        ]}
      />

      {tab === 'specifications' ? <SpecTab moduleId={id} onChanged={module.reload} /> : null}
      {tab === 'credentials' ? <CredentialTab moduleId={id} adapterKey={module.data.adapter} /> : null}
      {tab === 'operations' ? <OperationTab moduleId={id} /> : null}
      {tab === 'identities' && canImpersonate ? <IdentityTab moduleId={id} /> : null}

      {deleting ? (
        <ConfirmDelete
          title="Delete module"
          entity={
            <>
              Delete the module <strong>{module.data.name}</strong> and with it the route{' '}
              <code>/gw/{module.data.key}</code>?
            </>
          }
          impact={[
            'Every application loses its access - calls against this route answer 404 from now on.',
            'Imported specifications, the derived operations and every permission on them are gone.',
            'Stored credentials for the target API are deleted; the account there stays untouched.',
            'Consents users have given for this module fall away, as do their identity mappings.',
          ]}
          confirmText={module.data.key}
          confirmLabel="Delete module"
          onClose={() => setDeleting(false)}
          onConfirm={async () => {
            await api.delete(`/api/admin/modules/${id}`);
            navigate('/modules');
          }}
        />
      ) : null}
    </>
  );
}

function SpecTab({ moduleId, onChanged }: { moduleId: string; onChanged: () => void }) {
  const specs = useAsync(() => api.get<SpecVersion[]>(`/api/admin/modules/${moduleId}/specs`), [moduleId]);
  const [importing, setImporting] = useState(false);
  const [diff, setDiff] = useState<{ specId: string; diff: SpecDiff } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function showDiff(specId: string): Promise<void> {
    setError(null);
    try {
      const result = await api.get<SpecDiff>(`/api/admin/modules/${moduleId}/specs/${specId}/diff`);
      setDiff({ specId, diff: result });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  async function activate(specId: string): Promise<void> {
    setError(null);
    try {
      await api.post(`/api/admin/modules/${moduleId}/specs/${specId}/activate`);
      setDiff(null);
      specs.reload();
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Card
        title="Uploaded specifications"
        hint="Exactly one is active and determines which routes exist"
        actions={
          <button className="primary small" onClick={() => setImporting(true)}>
            Import specification
          </button>
        }
        tight
      >
        {specs.data?.length ? (
          <table>
            <thead>
              <tr>
                <th>Version</th>
                <th className="num">Operations</th>
                <th>Imported</th>
                <th>By</th>
                <th>Checksum</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {specs.data.map((spec) => (
                <tr key={spec.id}>
                  <td>
                    {spec.label} {spec.is_active ? <Badge tone="ok">active</Badge> : null}
                  </td>
                  <td className="num">{spec.operation_count}</td>
                  <td className="nowrap">{formatDateTime(spec.imported_at)}</td>
                  <td className="muted">{spec.imported_by ?? '—'}</td>
                  <td className="mono muted">{spec.checksum.slice(0, 12)}</td>
                  <td>
                    {spec.is_active ? null : (
                      <button className="small" onClick={() => void showDiff(spec.id)}>
                        Compare and activate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No specification imported yet. Without one the module accepts no requests.</Empty>
        )}
      </Card>

      {importing ? (
        <ImportDialog
          moduleId={moduleId}
          onClose={() => setImporting(false)}
          onImported={(result) => {
            setImporting(false);
            specs.reload();
            setDiff({ specId: result.versionId, diff: result.diff });
          }}
        />
      ) : null}

      {diff ? (
        <Modal
          title="What changes on activation"
          onClose={() => setDiff(null)}
          footer={
            <>
              <button onClick={() => setDiff(null)}>Cancel</button>
              <button className="primary" onClick={() => void activate(diff.specId)}>
                Activate now
              </button>
            </>
          }
        >
          <div className="grid cols-4" style={{ marginBottom: 16 }}>
            <div className="stat">
              <div className="label">Added</div>
              <div className="value">{diff.diff.added.length}</div>
            </div>
            <div className="stat">
              <div className="label">Removed</div>
              <div className="value">{diff.diff.removed.length}</div>
            </div>
            <div className="stat">
              <div className="label">Changed</div>
              <div className="value">{diff.diff.changed.length}</div>
            </div>
            <div className="stat">
              <div className="label">Unchanged</div>
              <div className="value">{diff.diff.unchanged}</div>
            </div>
          </div>

          {diff.diff.affectedGrants.length ? (
            <Notice tone="warn" title="Affected permissions">
              {diff.diff.affectedGrants.length} granted permissions point at operations that will no longer
              exist. They remain in place, but lead nowhere.
            </Notice>
          ) : null}

          {diff.diff.removed.length ? (
            <>
              <h4>Removed operations</h4>
              <pre className="payload">{diff.diff.removed.join('\n')}</pre>
            </>
          ) : null}
          {diff.diff.added.length ? (
            <>
              <h4>New operations</h4>
              <pre className="payload">{diff.diff.added.slice(0, 200).join('\n')}</pre>
            </>
          ) : null}
        </Modal>
      ) : null}
    </>
  );
}

function ImportDialog({
  moduleId,
  onClose,
  onImported,
}: {
  moduleId: string;
  onClose: () => void;
  onImported: (result: { versionId: string; diff: SpecDiff }) => void;
}) {
  const [text, setText] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const spec = JSON.parse(text);
      const result = await api.post<{ version: { id: string }; diff: SpecDiff }>(
        `/api/admin/modules/${moduleId}/specs`,
        { spec, label: label || undefined },
      );
      onImported({ versionId: result.version.id, diff: result.diff });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  async function loadFile(file: File): Promise<void> {
    setText(await file.text());
    if (!label) setLabel(file.name.replace(/\.json$/i, ''));
  }

  return (
    <Modal
      title="Import specification"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={() => void submit()} disabled={busy || !text}>
            Import
          </button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Field label="File" hint="OpenAPI 3 or Swagger 2.0 as JSON">
        <input
          type="file"
          accept="application/json,.json"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void loadFile(file);
          }}
        />
      </Field>
      <Field label="Label" hint="optional">
        <input type="text" value={label} onChange={(event) => setLabel(event.target.value)} />
      </Field>
      <Field label="Content">
        <textarea value={text} onChange={(event) => setText(event.target.value)} placeholder='{ "openapi": "3.0.0", ... }' />
      </Field>
      <p className="muted tiny">
        The import creates the version but does not activate it yet. First you see what changes.
      </p>
    </Modal>
  );
}

function CredentialTab({ moduleId, adapterKey }: { moduleId: string; adapterKey: string }) {
  const credentials = useAsync(
    () => api.get<Credential[]>(`/api/admin/modules/${moduleId}/credentials`),
    [moduleId],
  );
  const adapters = useAsync(() => api.get<AdapterInfo[]>('/api/admin/modules/adapters'));
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState<CheckMessage | null>(null);

  const adapter = adapters.data?.find((entry) => entry.key === adapterKey);

  async function check(credentialId: string): Promise<void> {
    try {
      const result = await api.post<CredentialCheck>(
        `/api/admin/modules/${moduleId}/credentials/${credentialId}/check`,
      );
      setMessage(describeCheck(result));
      credentials.reload();
    } catch (cause) {
      setMessage({ tone: 'danger', text: cause instanceof Error ? cause.message : String(cause) });
    }
  }

  async function setState(credentialId: string, state: 'active' | 'disabled'): Promise<void> {
    await api.patch(`/api/admin/modules/${moduleId}/credentials/${credentialId}/state`, { state });
    credentials.reload();
  }

  async function remove(credentialId: string): Promise<void> {
    if (!window.confirm('Really delete this credential?')) return;
    await api.delete(`/api/admin/modules/${moduleId}/credentials/${credentialId}`);
    credentials.reload();
  }

  return (
    <>
      {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}

      <Notice tone="info" title="Round-robin">
        Requests go in turn to the least recently used credential. Several credentials multiply the quota of the
        target API. After a 429 a credential is skipped until Retry-After has elapsed.
      </Notice>

      <Card
        title="Credentials"
        actions={
          <button className="primary small" onClick={() => setAdding(true)}>
            Store credential
          </button>
        }
        tight
      >
        {credentials.data?.length ? (
          <table>
            <thead>
              <tr>
                <th>Label</th>
                <th>State</th>
                <th>Zone</th>
                <th className="num">Calls</th>
                <th>Last used</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {credentials.data.map((credential) => (
                <tr key={credential.id}>
                  <td>
                    {credential.label}
                    {credential.hint ? <span className="muted mono"> {credential.hint}</span> : null}
                  </td>
                  <td>
                    <Badge tone={credential.state === 'active' ? 'ok' : credential.state === 'suspended' ? 'warn' : ''}>
                      {credential.state}
                    </Badge>
                    {credential.suspend_reason ? (
                      <div className="muted tiny">
                        {credential.suspend_reason}
                      </div>
                    ) : null}
                  </td>
                  <td className="mono muted">{credential.zone_base_url ?? 'unknown'}</td>
                  <td className="num">{Number(credential.use_count).toLocaleString('en-GB')}</td>
                  <td className="muted nowrap">{relativeTime(credential.last_used_at)}</td>
                  <td>
                    <div className="row">
                      <button className="small" onClick={() => void check(credential.id)}>
                        Check
                      </button>
                      <button
                        className="small"
                        onClick={() => void setState(credential.id, credential.state === 'disabled' ? 'active' : 'disabled')}
                      >
                        {credential.state === 'disabled' ? 'Enable' : 'Disable'}
                      </button>
                      <button className="small danger" onClick={() => void remove(credential.id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>Without a credential the gateway cannot forward anything.</Empty>
        )}
      </Card>

      {adding && adapter ? (
        <AddCredentialDialog
          moduleId={moduleId}
          adapter={adapter}
          onClose={() => setAdding(false)}
          onAdded={(result) => {
            setAdding(false);
            setMessage(describeCheck(result));
            credentials.reload();
          }}
        />
      ) : null}
    </>
  );
}

function AddCredentialDialog({
  moduleId,
  adapter,
  onClose,
  onAdded,
}: {
  moduleId: string;
  adapter: AdapterInfo;
  onClose: () => void;
  onAdded: (result: CredentialCheck) => void;
}) {
  const [label, setLabel] = useState('');
  const [secret, setSecret] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<CredentialCheck>(
        `/api/admin/modules/${moduleId}/credentials`,
        { label, secret },
      );
      onAdded(result);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  const complete = adapter.secretFields.every((field) => !field.required || secret[field.name]);

  return (
    <Modal
      title="Store credential"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={() => void submit()} disabled={busy || !label || !complete}>
            Save and check
          </button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Field label="Label" hint="appears in the audit log">
        <input type="text" value={label} onChange={(event) => setLabel(event.target.value)} placeholder="gw-api-1" />
      </Field>
      {adapter.secretFields.map((field) => (
        <Field key={field.name} label={field.label} hint={field.hint}>
          <input
            type={field.secret ? 'password' : 'text'}
            value={secret[field.name] ?? ''}
            onChange={(event) => setSecret({ ...secret, [field.name]: event.target.value })}
          />
        </Field>
      ))}
      <p className="muted tiny">
        The credentials are stored encrypted and never shown again - only the last four characters for
        recognition. Whether this user may impersonate in the target API can only be established by a real write
        operation and therefore stays open.
      </p>
    </Modal>
  );
}

function OperationTab({ moduleId }: { moduleId: string }) {
  const [search, setSearch] = useState('');
  const operations = useAsync(
    () => api.get<Operation[]>(`/api/admin/modules/${moduleId}/operations?search=${encodeURIComponent(search)}`),
    [moduleId, search],
  );

  return (
    <Card title="Operations of the active specification" hint="at the same time the permission atoms" tight>
      <div className="matrix-toolbar">
        <input
          type="search"
          placeholder="Filter resource or path"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <span className="muted">{operations.data?.length ?? 0} operations</span>
      </div>
      {operations.data?.length ? (
        <div className="matrix-scroll">
          <table>
            <thead>
              <tr>
                <th>Permission</th>
                <th>Method</th>
                <th>Path</th>
                <th>Kind</th>
                <th>Impersonation</th>
              </tr>
            </thead>
            <tbody>
              {operations.data.map((operation) => (
                <tr key={operation.id}>
                  <td className="mono">{operation.operation_key}</td>
                  <td className="mono">{operation.http_method}</td>
                  <td className="mono muted">{operation.path_template}</td>
                  <td>
                    <Badge tone={operation.is_write ? 'warn' : ''}>
                      {operation.is_write ? 'writing' : 'reading'}
                    </Badge>
                  </td>
                  <td>{operation.supports_impersonation ? <Badge tone="accent">possible</Badge> : <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Empty>No operations found.</Empty>
      )}
    </Card>
  );
}

function IdentityTab({ moduleId }: { moduleId: string }) {
  const identities = useAsync(
    () => api.get<IdentityRow[]>(`/api/admin/modules/${moduleId}/identities`),
    [moduleId],
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState<IdentityRow | null>(null);

  async function sync(): Promise<void> {
    setBusy(true);
    try {
      const result = await api.post<{ checked: number; ok: number; problems: number }>(
        `/api/admin/modules/${moduleId}/identities/sync`,
      );
      setMessage(`${result.ok} of ${result.checked} mapped, ${result.problems} open.`);
      identities.reload();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  const open = identities.data?.filter((row) => row.status !== 'ok') ?? [];

  return (
    <>
      <Notice tone="info" title="Why this matters">
        This API can name the person who caused a write. That is why the gateway insists on knowing who
        that is: a record under the wrong name would be worse than an error somebody can fix. Whether
        someone may write at all is decided by the permission model, not here — and for APIs that cannot
        attribute a write, no mapping is required.
      </Notice>

      {message ? <Notice tone="ok">{message}</Notice> : null}

      <Card
        title="Mappings"
        hint={`${open.length} open`}
        actions={
          <button className="small" onClick={() => void sync()} disabled={busy}>
            {busy ? 'Running...' : 'Start sync'}
          </button>
        }
        tight
      >
        {identities.data?.length ? (
          <table>
            <thead>
              <tr>
                <th>Person</th>
                <th>Target system</th>
                <th>State</th>
                <th>Origin</th>
                <th>Last checked</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {identities.data.map((row) => (
                <tr key={row.user_id}>
                  <td>
                    {row.display_name ?? row.email}
                    <div className="muted tiny">
                      {row.email}
                    </div>
                  </td>
                  <td className="mono">
                    {row.upstream_id ?? '—'}
                    {row.upstream_label ? (
                      <div className="muted tiny">
                        {row.upstream_label}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <Badge tone={row.status === 'ok' ? 'ok' : row.status === 'ambiguous' ? 'danger' : 'warn'}>
                      {row.status ?? 'not checked'}
                    </Badge>
                  </td>
                  <td className="muted">{row.source ?? '—'}</td>
                  <td className="muted nowrap">{relativeTime(row.resolved_at)}</td>
                  <td>
                    <button className="small" onClick={() => setEditing(row)}>
                      Map
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>Nobody has signed in yet.</Empty>
        )}
      </Card>

      {editing ? (
        <SetIdentityDialog
          moduleId={moduleId}
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            identities.reload();
          }}
        />
      ) : null}
    </>
  );
}

function SetIdentityDialog({
  moduleId,
  row,
  onClose,
  onSaved,
}: {
  moduleId: string;
  row: IdentityRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [upstreamId, setUpstreamId] = useState(row.upstream_id ?? '');
  const [label, setLabel] = useState(row.upstream_label ?? '');
  const [error, setError] = useState<string | null>(null);

  const candidates = ((row.detail?.candidates as Array<{ upstreamId: string; label: string }>) ?? []).slice(0, 10);

  async function save(): Promise<void> {
    try {
      await api.put(`/api/admin/modules/${moduleId}/identities/${row.user_id}`, {
        upstreamId,
        upstreamLabel: label || undefined,
      });
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  async function resolve(): Promise<void> {
    try {
      await api.post(`/api/admin/modules/${moduleId}/identities/${row.user_id}/resolve`);
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <Modal
      title={`Mapping for ${row.email ?? row.user_id}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={() => void resolve()}>Search automatically</button>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={() => void save()} disabled={!upstreamId}>
            Map permanently
          </button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {candidates.length ? (
        <>
          <p className="muted">Candidates found:</p>
          <table>
            <tbody>
              {candidates.map((candidate) => (
                <tr key={candidate.upstreamId}>
                  <td className="mono">{candidate.upstreamId}</td>
                  <td>{candidate.label}</td>
                  <td>
                    <button
                      className="small"
                      onClick={() => {
                        setUpstreamId(candidate.upstreamId);
                        setLabel(candidate.label);
                      }}
                    >
                      Use
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
      <Field label="Identifier in the target system" hint="for Autotask the resource id">
        <input type="text" value={upstreamId} onChange={(event) => setUpstreamId(event.target.value)} />
      </Field>
      <Field label="Display name" hint="optional, only for recognition">
        <input type="text" value={label} onChange={(event) => setLabel(event.target.value)} />
      </Field>
      <p className="muted tiny">
        A mapping set by hand always wins and is never overwritten by the nightly sync.
      </p>
    </Modal>
  );
}

interface CredentialCheck {
  reachable: boolean;
  message: string;
  /**
   * Set when the base URL repeats a prefix the specification's paths already
   * carry. Reachable and wrong at the same time is exactly the case a bare
   * "ok" would hide.
   */
  warning?: string | null;
}

interface CheckMessage {
  tone: 'ok' | 'warn' | 'danger';
  text: string;
}

function describeCheck(result: CredentialCheck): CheckMessage {
  if (!result.reachable) return { tone: 'danger', text: result.message };
  if (result.warning) return { tone: 'warn', text: `${result.message} ${result.warning}` };
  return { tone: 'ok', text: result.message };
}
